/* Keep the finding, drop the prose.
 *
 * Lives in the engine package because it is about the shape of engine output,
 * and because both sides need it: the browser strips before sending so the
 * policy language never crosses the wire, and the backend strips again before
 * storing so a caller that forgot cannot leave prose in the database. Running
 * it twice is harmless, the markers being far shorter than the threshold.
 *
 * The derived objects are conclusions — which coverage parts exist, what the
 * limits are, whether a trigger is occurrence or claims-made — and they are
 * worth keeping. But some carry the policy language they were read from, and
 * `endorsements` carries a lot of it: 595 kB across eight reviews, whole
 * endorsement pages verbatim, named `text`. That is the document, retained
 * under another name, and it defeats the point of not storing the document.
 *
 * So long free-text fields are replaced with a marker giving their length. A
 * marker rather than deletion, because a reader of the stored object should be
 * able to see that something was cut and how much — silently shorter data is
 * how people come to believe a field never existed.
 *
 * Short values stay. "Claims-Made", "Full Prior Acts Coverage" and
 * "ECC-545-0712" are labels and form numbers, not policy prose, and stripping
 * them would leave a finding nobody could act on.
 *
 * The report is generated in the same request, from the live text, so nothing
 * here affects what the broker reads. It only affects what is kept.
 */

// Fields that hold the language a finding was read from.
const PROSE_KEYS = new Set([
  'text', 'excerpt', 'snippet', 'quote', 'context', 'raw', 'rawText',
  'matchedText', 'sentence', 'passage', 'body'
]);

// Long enough to be prose rather than a label. A form number is ~12
// characters, a trigger basis ~11, a retro date ~24.
export const PROSE_MIN = 120;

export function redactForStorage(value, key) {
  if (Array.isArray(value)) return value.map(v => redactForStorage(v));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = redactForStorage(v, k);
    return out;
  }
  if (typeof value === 'string' && key && PROSE_KEYS.has(key) && value.length >= PROSE_MIN) {
    return `[not retained — ${value.length} characters of policy text]`;
  }
  return value;
}

/** How much prose a redaction removed. For logging, so the saving is visible
 *  rather than asserted. */
export function redactionSaving(before, after) {
  return JSON.stringify(before).length - JSON.stringify(after).length;
}
